"use client";
import React, { useState, useMemo, useRef } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import SliderComponent from "./SliderComponent";
import ImageMagnifier from "../../../../../../components/ImageMagnifier/ImageMagnifier";

// Import Swiper styles
import "swiper/css";
import { useDictionary } from "@/components/DictProvider/DictProvider";
import toast from "react-hot-toast";
import NotifyButton from "@/components/Product/NotifyButton/NotifyButton";
import isImage from "@/lib/isImage";
import VideoPlayer from "./VideoPlayer";
import ImageSliderDialog from "./ImageSliderDialog";
import { Button } from "@mui/material";
import useImageUrlModifier from "@/lib/useImageUrlModifier";

interface ProductSliderProps {
  picture: string[];
  product: any;
  thumbnailImage?: string | null;
}

const ProductSlider: React.FC<ProductSliderProps> = ({
  product,
  picture,
  thumbnailImage,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [fullScreenImg, setFullScreenImg] = useState<boolean>(false);
  const swiperRef = useRef<SwiperType | null>(null);
  const translate = useDictionary();
  const isStock = product?.data?.stock > 0;

  // Fallback image
  const fallbackImage = "/images/dummy-image-square.jpg";

  const images = useMemo(
    () => (picture?.length > 0 ? picture.filter(Boolean) : [fallbackImage]),
    [picture]
  );
  // Handle previous button click
  const handlePrev = () => {
    if (swiperRef?.current) {
      const currentIndex =
        activeIndex > 0 ? activeIndex - 1 : images.length - 1;
      setActiveIndex(currentIndex); // Update the state manually
      swiperRef.current.slideTo(currentIndex); // Update the main swiper
    }
  };

  // Handle next button click
  const handleNext = () => {
    if (swiperRef?.current) {
      const currentIndex =
        activeIndex < images.length - 1 ? activeIndex + 1 : 0; // Loop back
      setActiveIndex(currentIndex); // Update the state manually
      swiperRef.current.slideTo(currentIndex); // Update the main swiper
    }
  };

  // Handle Share
  const handleShare = async () => {
    const shareData = {
      title: product?.data?.language?.brand,
      text: product?.data?.language?.product,
      url: window.location.href,
    };
    try {
      await navigator.share(shareData);
    } catch (err) {
      toast.error("can't share");
    }
  };

  const fullScreenHandler = () => {
    setFullScreenImg(true);
  };

  const modifyImageUrl = useImageUrlModifier();

  return (
    <div className="relative w-full flex flex-row items-start gap-4 h-fit">
      {/* Thumbnails with navigation arrows for desktop */}
      {/* {product?.data?.stock > 0 && ( */}
      <div className="hidden md:flex justify-start flex-col items-center h-fit gap-2">
        {/* prev button */}
        <Button
          disabled={activeIndex === 0 || images?.length < 4}
          onClick={handlePrev}
          sx={{ bgcolor: "#ededed" }}
          aria-label="Prev"
          className="p-2 flex justify-center disabled:invisible items-center rounded-sm w-full hover:text-primary"
        >
          <svg
            width="22"
            height="12"
            viewBox="0 0 24 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.29394 11.7306C0.106106 11.5577 0.000376252 11.323 1.00225e-06 11.0782C-0.000374248 10.8334 0.104636 10.5985 0.291939 10.4251L11.2256 0.298558C11.3278 0.203923 11.4493 0.128837 11.5831 0.0776044C11.7168 0.026372 11.8602 0 12.005 0C12.1498 0 12.2932 0.026372 12.427 0.0776044C12.5607 0.128837 12.6822 0.203923 12.7844 0.298558L23.7201 10.4251C23.9021 10.5994 24.0026 10.8328 23.9999 11.0749C23.9973 11.317 23.8917 11.5484 23.706 11.7194C23.5202 11.8903 23.2691 11.9871 23.0067 11.9888C22.7444 11.9906 22.4918 11.8972 22.3034 11.7288L12.006 2.19313L1.70867 11.7288C1.52131 11.9021 1.26702 11.9997 1.00172 12C0.73642 12.0003 0.481831 11.9034 0.29394 11.7306Z"
              fill="black"
              fillOpacity="0.8"
            />
          </svg>
        </Button>

        {/* Thumbnail Swiper */}
        <Swiper
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          spaceBetween={4}
          slidesPerView={4}
          direction="vertical"
          className="max-h-fit h-[292px] w-full"
        >
          {images.map((image, index) => (
            <SwiperSlide
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`relative cursor-pointer !w-[70px] !h-[70px] !aspect-square flex justify-center items-center border-2 ${
                index === activeIndex
                  ? "border-primary"
                  : "border-secondary hover:border-primary/50"
              }`}
            >
              <>
                {isImage(image) ? (
                  <Image
                    src={modifyImageUrl(image, 150, 150) || fallbackImage}
                    alt={`Thumbnail ${index + 1}`}
                    fill={true}
                    className="object-center"
                    placeholder="blur"
                    blurDataURL={fallbackImage} // Low-quality version for blur
                    sizes="70px"
                  />
                ) : (
                  <div className="flex justify-center items-center">
                    <video
                      className="w-full aspect-square object-contain"
                      muted
                      controls={false}
                      autoPlay={false}
                      src={modifyImageUrl(image, 0, 0, true) || ""}
                    />
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] flex items-center justify-center cursor-pointer"
                    >
                      <path
                        d="M9.5 15.5851V8.41714C9.50015 8.32745 9.52442 8.23946 9.57026 8.16238C9.61611 8.0853 9.68184 8.02197 9.76057 7.97902C9.83931 7.93608 9.92814 7.9151 10.0178 7.91828C10.1074 7.92147 10.1945 7.94871 10.27 7.99714L15.846 11.5801C15.9166 11.6254 15.9747 11.6876 16.0149 11.7612C16.0552 11.8348 16.0763 11.9173 16.0763 12.0011C16.0763 12.085 16.0552 12.1675 16.0149 12.2411C15.9747 12.3146 15.9166 12.3769 15.846 12.4221L10.27 16.0061C10.1945 16.0546 10.1074 16.0818 10.0178 16.085C9.92814 16.0882 9.83931 16.0672 9.76057 16.0243C9.68184 15.9813 9.61611 15.918 9.57026 15.8409C9.52442 15.7638 9.50015 15.6758 9.5 15.5861V15.5851Z"
                        fill="#ededed"
                      />
                      <path
                        d="M1 12C1 5.925 5.925 1 12 1C18.075 1 23 5.925 23 12C23 18.075 18.075 23 12 23C5.925 23 1 18.075 1 12ZM12 2.5C9.48044 2.5 7.06408 3.50089 5.28249 5.28249C3.50089 7.06408 2.5 9.48044 2.5 12C2.5 14.5196 3.50089 16.9359 5.28249 18.7175C7.06408 20.4991 9.48044 21.5 12 21.5C14.5196 21.5 16.9359 20.4991 18.7175 18.7175C20.4991 16.9359 21.5 14.5196 21.5 12C21.5 9.48044 20.4991 7.06408 18.7175 5.28249C16.9359 3.50089 14.5196 2.5 12 2.5Z"
                        fill="#ededed"
                      />
                    </svg>
                  </div>
                )}
              </>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* next button */}
        <Button
          disabled={activeIndex === images.length - 1 || images?.length < 4}
          onClick={handleNext}
          sx={{ bgcolor: "#ededed" }}
          aria-label="Next"
          className="p-2 flex justify-center disabled:invisible items-center rounded-sm w-full hover:text-primary"
        >
          <svg
            width="22"
            height="12"
            viewBox="0 0 24 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.29394 0.269403C0.106106 0.442298 0.000376252 0.676957 1.00225e-06 0.921778C-0.000374248 1.1666 0.104636 1.40153 0.291939 1.57492L11.2256 11.7014C11.3278 11.7961 11.4493 11.8712 11.5831 11.9224C11.7168 11.9736 11.8602 12 12.005 12C12.1498 12 12.2932 11.9736 12.427 11.9224C12.5607 11.8712 12.6822 11.7961 12.7844 11.7014L23.7201 1.57492C23.9021 1.40055 24.0026 1.16719 23.9999 0.925106C23.9973 0.683019 23.8917 0.451574 23.706 0.280627C23.5202 0.109679 23.2691 0.012907 23.0067 0.0111532C22.7444 0.00939846 22.4918 0.102802 22.3034 0.271248L12.006 9.80687L1.70867 0.271248C1.52131 0.0979147 1.26702 0.000347137 1.00172 9.53674e-07C0.73642 -0.00034523 0.481831 0.0965586 0.29394 0.269403Z"
              fill="black"
              fillOpacity="0.8"
            />
          </svg>
        </Button>
      </div>
      {/* // )} */}

      {/* Main Image for desktop */}
      <div className="relative hidden md:block h-full w-full max-h-[400px] max-w-[400px] aspect-square border border-secondary">
        {product?.data?.stock > 0
          ? images.map(
              (image, index) =>
                activeIndex === index && (
                  <div key={index}>
                    {isImage(image) || thumbnailImage ? (
                      <div onClick={fullScreenHandler}>
                        <ImageMagnifier
                          key={index}
                          src={thumbnailImage || image || fallbackImage}
                          initialZoomLevel={2}
                        />
                      </div>
                    ) : (
                      <VideoPlayer
                        videoUrl={modifyImageUrl(image, 0, 0, true) || ""}
                        play={false}
                        stock={product?.data?.stock === 0 ? true : false}
                        productId={product?.data?.id}
                      />
                    )}
                  </div>
                )
            )
          : images.map(
              (image, index) =>
                activeIndex === index && (
                  <div key={index}>
                    {isImage(image) || thumbnailImage ? (
                      <>
                        <Image
                          fill={true}
                          src={
                            thumbnailImage
                              ? modifyImageUrl(thumbnailImage, 500, 500) ||
                                fallbackImage
                              : image
                              ? modifyImageUrl(image, 500, 500) ||
                                fallbackImage
                              : fallbackImage
                          }
                          alt="outOfStock "
                          className="object-cover"
                          placeholder="blur"
                          blurDataURL={fallbackImage} // Low-quality version for blur
                          sizes="auto"
                        />{" "}
                        <div className="w-full h-full absolute inset-0 bg-[#E0E0E0]/50 rounded-md p-4">
                          <div className=" relative top-0 flex flex-col justify-center items-center left-0 right-0 text-white font-semibold w-full h-full aspect-square text-center">
                            <p className=" bg-[#555555] py-1 px-4 absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]">
                              {translate.poductDescriptionPage.outOfStock}
                            </p>
                            <div className="absolute bottom-0 right-0">
                              <NotifyButton productId={product?.data?.id} />
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <VideoPlayer
                        videoUrl={modifyImageUrl(image, 0, 0, true) || ""}
                        play={false}
                        stock={product?.data?.stock === 0 ? true : false}
                        productId={product?.data?.id}
                      />
                    )}
                  </div>
                )
            )}
        <div
          className="absolute ltr:-right-12 rtl:-left-12 cursor-pointer top-0"
          onClick={handleShare}
        >
          <Image
            src={"/images/shareIcon.svg"}
            width={28}
            height={28}
            alt="share"
          />
        </div>
        {/* BOGO STICKER */}
        {!!product?.data?.bogo && !!product?.data?.sticker && (
          <div className="absolute z-10 bottom-0 right-0 m-3">
            <Image
              layout="responsive"
              width={102}
              height={36}
              src={product?.data?.sticker?.replace(
                /^https:\/\/res\.cloudinary\.com/,
                "https://res.cloudinary.com"
              )}
              alt="bogo"
              className="object-contain"
            />
          </div>
        )}
      </div>

      {/* slider for mobile phones */}
      <div className="relative md:hidden w-full">
        <SliderComponent
          images={images}
          fallbackImage={fallbackImage}
          product={product}
          handleShare={handleShare}
          onImageClick={isStock ? fullScreenHandler : undefined}
        />
      </div>

      {fullScreenImg && isStock && (
        <ImageSliderDialog
          open={fullScreenImg}
          onClose={() => setFullScreenImg(false)}
          images={images}
          fallbackImage={fallbackImage}
          isStock={product?.data?.stock === 0}
          product={product}
        />
      )}
    </div>
  );
};

export default ProductSlider;
